import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';

import { JobWorkflowAutomationsHttpClient } from './job-workflow-automations-http.service';
import { JobWorkflowAutomationsActions } from './job-workflow-automations.store';

@Injectable({ providedIn: 'root' })
export class JobWorkflowAutomationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly jobWorkflowAutomationsHttpClient = inject(JobWorkflowAutomationsHttpClient);

  public effect = createEffectFactory(JobWorkflowAutomationsActions, this.actions$);

  public fetchJobAutomations$ = this.effect(
    JobWorkflowAutomationsActions.fetchJobAutomations,
    ({ payload }) =>
      this.jobWorkflowAutomationsHttpClient.loadJobAutomations(payload.jobId).pipe(
        map(data =>
          JobWorkflowAutomationsActions.fetchJobAutomationsSuccess({
            payload: { data, scope: payload.scope },
          }),
        ),
        catchError((error: HttpErrorResponse) =>
          of(
            JobWorkflowAutomationsActions.fetchJobAutomationsFailed({
              payload: { id: payload.jobId, error },
            }),
          ),
        ),
      ),
  );

  public create$ = this.effect(JobWorkflowAutomationsActions.createOnePessimistic, ({ payload }) =>
    this.jobWorkflowAutomationsHttpClient
      .createAutomation(payload.data, payload.jobId)
      .pipe(map(data => ({ data, scope: payload.scope, collectionId: payload.correlationId }))),
  );

  public update$ = this.effect(JobWorkflowAutomationsActions.updateOnePessimistic, ({ payload }) =>
    this.jobWorkflowAutomationsHttpClient
      .updateAutomation(payload.data, payload.jobId)
      .pipe(map(data => ({ data, scope: payload.scope, collectionId: payload.correlationId }))),
  );

  public remove$ = this.effect(JobWorkflowAutomationsActions.removeOnePessimistic, ({ payload }) =>
    this.jobWorkflowAutomationsHttpClient
      .removeAutomation(payload.data, payload.jobId)
      .pipe(map(data => ({ data }))),
  );

  public duplicate$ = this.effect(JobWorkflowAutomationsActions.duplicate, ({ payload }) =>
    this.jobWorkflowAutomationsHttpClient
      .duplicateAutomation(payload.data, payload.additionalData)
      .pipe(
        switchMap(data => [
          JobWorkflowAutomationsActions.duplicateSuccess({
            payload: { data, scope: payload.scope },
          }),
        ]),
        catchError(error => [JobWorkflowAutomationsActions.duplicateFailed({ payload: error })]),
      ),
  );

  public fetchOptions$ = this.effect(JobWorkflowAutomationsActions.fetchOptions, ({ payload }) =>
    this.jobWorkflowAutomationsHttpClient.fetchAutomationOptions(payload.id).pipe(
      switchMap(response => [
        JobWorkflowAutomationsActions.fetchOptionsSuccess({
          payload: response,
        }),
      ]),
      catchError(error => [
        JobWorkflowAutomationsActions.fetchOptionsFailed({
          payload: { id: payload.id, error },
        }),
      ]),
    ),
  );

  public fetchTemplates$ = this.effect(
    JobWorkflowAutomationsActions.fetchAutomationTemplates,
    ({ payload }) =>
      this.jobWorkflowAutomationsHttpClient.fetchAutomationTemplates(payload.id).pipe(
        switchMap(response => [
          JobWorkflowAutomationsActions.fetchAutomationTemplatesSuccess({
            payload: response,
          }),
        ]),
        catchError(error => [
          JobWorkflowAutomationsActions.fetchAutomationTemplatesFailed({
            payload: { id: payload.id, error },
          }),
        ]),
      ),
  );
}
